// @ts-check
/** @typedef {import('../types/api').Task} Task */

import { isPast, isThisWeek, isToday } from 'date-fns';
import { AlertCircle, Calendar, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import TaskCard from '../components/tasks/TaskCard';
import TaskDetailModal from '../components/tasks/TaskDetailModal';
import taskService from '../services/taskService';

const MyTasksPage = () => {
  /** @type {[Task[], Function]} */
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser(user);
      loadMyTasks(user.id);
    }
  }, []);

  const loadMyTasks = async (userId) => {
    setLoading(true);
    try {
      const data = await taskService.getTasks({ assigned_to: userId });
      setTasks(data.filter((t) => t.status !== 'done'));
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedTasks = {
    overdue: tasks.filter((t) => t.due_date && isPast(new Date(t.due_date)) && !isToday(new Date(t.due_date))),
    today: tasks.filter((t) => t.due_date && isToday(new Date(t.due_date))),
    thisWeek: tasks.filter((t) => t.due_date && isThisWeek(new Date(t.due_date)) && !isToday(new Date(t.due_date))),
    noDueDate: tasks.filter((t) => !t.due_date),
  };

  const handleTaskUpdate = () => {
    if (currentUser) {
      loadMyTasks(currentUser.id);
    }
  };

  return (
    <div className="min-h-screen bg-muted/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-foreground mb-8">My Tasks</h1>

        {loading ? (
          <LoadingScreen />
        ) : (
          <div className="space-y-8">
            {/* Overdue */}
            {groupedTasks.overdue.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                  <AlertCircle size={20} />
                  Overdue ({groupedTasks.overdue.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedTasks.overdue.map((task) => (
                    <TaskCard key={task.id} task={task} onClick={setSelectedTask} />
                  ))}
                </div>
              </div>
            )}

            {/* Due Today */}
            {groupedTasks.today.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-orange-600 dark:text-orange-400 mb-4">
                  <Calendar size={20} />
                  Due Today ({groupedTasks.today.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedTasks.today.map((task) => (
                    <TaskCard key={task.id} task={task} onClick={setSelectedTask} />
                  ))}
                </div>
              </div>
            )}

            {/* Due This Week */}
            {groupedTasks.thisWeek.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-blue-600 dark:text-blue-400 mb-4">
                  <Clock size={20} />
                  Due This Week ({groupedTasks.thisWeek.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedTasks.thisWeek.map((task) => (
                    <TaskCard key={task.id} task={task} onClick={setSelectedTask} />
                  ))}
                </div>
              </div>
            )}

            {/* No Due Date */}
            {groupedTasks.noDueDate.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-foreground/80 mb-4">
                  No Due Date ({groupedTasks.noDueDate.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedTasks.noDueDate.map((task) => (
                    <TaskCard key={task.id} task={task} onClick={setSelectedTask} />
                  ))}
                </div>
              </div>
            )}

            {tasks.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No tasks assigned to you</p>
              </div>
            )}
          </div>
        )}

        {selectedTask && (
          <TaskDetailModal
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onUpdate={handleTaskUpdate}
            currentUser={currentUser}
          />
        )}
      </div>
    </div>
  );
};

export default MyTasksPage;
